import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ShoppingCartIcon } from 'lucide-react';
type ExitIntentModalProps = {
  /** Fired when the visitor accepts the offer (e.g. continue to checkout). */
  onAccept: () => void;
  /** Optional callback when the modal is shown (e.g. fire a pixel event). */
  onShow?: () => void;
};
const SESSION_KEY = 'vsl_exit_intent_shown';
/**
 * Exit-intent recovery popup. Triggers when the visitor moves the cursor
 * toward the top of the viewport (desktop) or attempts to navigate back
 * (mobile / browser back button). Shows at most once per session.
 */
export function ExitIntentModal({
  onAccept,
  onShow
}: ExitIntentModalProps) {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);
  const trigger = () => {
    if (firedRef.current) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}
    firedRef.current = true;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {}
    setOpen(true);
    onShow?.();
  };
  useEffect(() => {
    // DESKTOP: cursor leaves through the top edge of the document.
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    document.addEventListener('mouseout', onMouseOut);
    // MOBILE / back button: push a state so the first back press opens the
    // modal instead of leaving the page.
    let pushed = false;
    try {
      window.history.pushState({
        exitGuard: true
      }, '');
      pushed = true;
    } catch {}
    const onPopState = () => trigger();
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('popstate', onPopState);
      if (pushed) {
        // best-effort cleanup; ignored if already navigated
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!open) return null;
  return <div role="dialog" aria-modal="true" aria-label="Espera, no te vayas" className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6" data-id="element-14">
      <style data-id="element-15">{`@keyframes exitPop{0%{transform:scale(.9)translateY(8px);opacity:0}60%{transform:scale(1.02)}100%{transform:scale(1)translateY(0);opacity:1}}.exit-pop{animation:exitPop .4s cubic-bezier(.34,1.56,.64,1) both}`}</style>

      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-id="element-16" />

      <div className="exit-pop relative w-full max-w-[480px] overflow-hidden rounded-2xl bg-[#0f172a] border border-[#1e293b] shadow-[0_30px_80px_rgba(0,0,0,0.6)] text-center" data-id="element-17">
        <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar" className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white transition-colors" data-id="element-18">
          <XIcon className="h-5 w-5" data-id="element-19" />
        </button>

        <div className="px-6 sm:px-9 py-9" data-id="element-20">
          <span className="inline-block rounded-full bg-[#36D9F4]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[1.5px] text-[#36D9F4] mb-4" data-id="element-21">
            Espera un momento
          </span>

          <h2 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(24px,6vw,34px)] leading-[1.05] text-white mb-3" data-id="element-22">
            ¿Te vas sin tu acceso a <span className="text-[#36D9F4]" data-id="element-23">$297</span>
            ?
          </h2>

          <p className="text-[14px] sm:text-[15px] leading-relaxed text-white/70 mb-6" data-id="element-24">
            Antes de irte: este es el mismo sistema con el que otros generaron
            su primer ingreso con AI en 48 horas — hoy a un precio que no
            volverás a ver. Asegura tu lugar antes de que termine.
          </p>

          <div className="flex items-center justify-center gap-3 mb-6 flex-wrap" data-id="element-25">
            <span className="font-['Anton'] text-[28px] text-white/50 line-through decoration-[#94a3b8]" data-id="element-26">
              $9,370
            </span>
            <span className="font-['Anton'] text-[40px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-white px-5 pt-1 pb-0.5 rounded-md" data-id="element-27">
              $297
            </span>
          </div>

          <button type="button" onClick={() => {
          setOpen(false);
          onAccept();
        }} className="relative overflow-hidden flex items-center justify-center gap-2.5 w-full bg-[#3FAC00] hover:bg-[#43B800] text-white font-extrabold text-[18px] tracking-tight p-4 rounded-lg transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.2)] active:shadow-[0_2px_0_#2C7A00]" data-id="element-28">
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-white/35 to-transparent" data-id="element-29" />
            <ShoppingCartIcon className="relative w-5 h-5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-30" />
            <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-31">
              Sí, quiero mi acceso ahora
            </span>
          </button>

          <button type="button" onClick={() => setOpen(false)} className="mt-4 text-[12.5px] text-white/40 hover:text-white/70 underline underline-offset-2 transition-colors" data-id="element-32">
            No gracias, prefiero perder esta oferta
          </button>
        </div>
      </div>
    </div>;
}